import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface FieldDef {
  type: string;
  label: string;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  keyboard?: string;
  required?: boolean;
  autocomplete?: string;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
  matchMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class FieldRegistryService {
  private fields: Record<string, Record<string, FieldDef>> = {};
  private loaded = false;

  constructor(private http: HttpClient) {}

  async load(): Promise<void> {
    if (this.loaded) return;
    try {
      const data = await firstValueFrom(
        this.http.get<Record<string, Record<string, FieldDef>>>('/assets/design-system/fields.json')
      );
      // Strip metadata keys
      const { $schema, $description, ...domains } = data as any;
      this.fields = domains;
      this.loaded = true;
    } catch (err) {
      console.warn('FieldRegistryService: Failed to load fields.json', err);
      this.loaded = true; // Don't block app if file is missing
    }
  }

  /**
   * Get field definition by domain and field name.
   * @param domain e.g. 'user', 'vehicle', 'gatepass'
   * @param name e.g. 'email', 'plate_number'
   */
  getField(domain: string, name: string): FieldDef | null {
    return this.fields[domain]?.[name] ?? null;
  }

  /**
   * Get field definition by composite key (domain.field_name).
   * @param key e.g. 'user.email', 'vehicle.plate_number'
   */
  getFieldByKey(key: string): FieldDef | null {
    const dotIdx = key.indexOf('.');
    if (dotIdx === -1) return null;
    const domain = key.substring(0, dotIdx);
    const name = key.substring(dotIdx + 1);
    return this.getField(domain, name);
  }
}
